import React, { useState, type ChangeEvent, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { portalBase } from '../lib/api';
import { ORG_TYPES, PAYOUT_ORG_TYPES, SignupError, submitSignup, type SignupForm } from '../lib/signUp';

/**
 * Partner-organization signup, the "Create an account" half of the sign-in page (route: /signup).
 * Contact and organization details go to the approval queue; the form is replaced by a notice that
 * the account awaits approval. First name, last name, email, organization name, at least one org
 * type and the ticked agreement gate submit — nothing else is required.
 */
const INPUT_CLASS = 'w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent disabled:opacity-50';
const LABEL_CLASS = 'block text-sm font-medium text-gray-700 mb-1';

const EMPTY: SignupForm = { firstName: '', lastName: '', email: '', phone: '', orgName: '', businessTypes: [], address: '', taxId: '' };

/** What the pending-approval notice repeats back from a signup that has committed. */
type PendingSignup = Pick<SignupForm, 'firstName' | 'email' | 'orgName'>;

const SignUpForm: React.FC = () => {
  const [form, setForm] = useState<SignupForm>(EMPTY);
  // Consent to the documents, not a field of the application: nothing sends it.
  const [agreed, setAgreed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState<PendingSignup | null>(null);

  const field = (name: keyof Omit<SignupForm, 'businessTypes'>) => ({
    id: `signup-${name}`,
    value: form[name],
    onChange: (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => setForm((prev) => ({ ...prev, [name]: e.target.value })),
    disabled: loading,
  });

  const toggleType = (value: string): void =>
    setForm((prev) => ({
      ...prev,
      businessTypes: prev.businessTypes.includes(value) ? prev.businessTypes.filter((t) => t !== value) : [...prev.businessTypes, value],
    }));

  // One payout type among several is enough to ask, so [distributor, hospital] gets the field too.
  const collectsTaxId = form.businessTypes.some((type) => PAYOUT_ORG_TYPES.includes(type));
  const isValid = agreed && [form.firstName, form.lastName, form.email, form.orgName].every((v) => v.trim()) && form.businessTypes.length > 0;

  const submit = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      await submitSignup(form);
      const { firstName, email, orgName } = form;
      setPending({ firstName: firstName.trim(), email: email.trim(), orgName: orgName.trim() });
    } catch (err) {
      setError(err instanceof SignupError ? err.message : 'Signup failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (pending) {
    return (
      <div className="mt-6 rounded-xl bg-blue-50 px-5 py-4 text-sm text-blue-800" role="status" data-testid="signup-pending">
        <p className="font-semibold">Thanks, {pending.firstName} — {pending.orgName} is waiting for approval.</p>
        <p className="mt-1">
          A Pawsitive Future administrator reviews every new account, usually in less than 24 hours. We&apos;ll email {pending.email} when
          it&apos;s approved, and you can <Link to="/sign-in" className="font-medium text-primary hover:underline">sign in</Link> then.
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="mt-6 space-y-6">
      <fieldset className="space-y-4">
        <legend className="text-lg font-semibold text-primary mb-2">Contact</legend>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label htmlFor="signup-firstName" className={LABEL_CLASS}>First name *</label>
            <input {...field('firstName')} className={INPUT_CLASS} autoComplete="given-name" required />
          </div>
          <div>
            <label htmlFor="signup-lastName" className={LABEL_CLASS}>Last name *</label>
            <input {...field('lastName')} className={INPUT_CLASS} autoComplete="family-name" required />
          </div>
          <div>
            <label htmlFor="signup-email" className={LABEL_CLASS}>Email address *</label>
            <input {...field('email')} className={INPUT_CLASS} type="email" inputMode="email" autoComplete="email" required />
          </div>
          <div>
            <label htmlFor="signup-phone" className={LABEL_CLASS}>Phone number</label>
            <input {...field('phone')} className={INPUT_CLASS} type="tel" inputMode="tel" autoComplete="tel" />
          </div>
        </div>
      </fieldset>

      <fieldset className="space-y-4">
        <legend className="text-lg font-semibold text-primary mb-2">Organization</legend>
        <div>
          <label htmlFor="signup-orgName" className={LABEL_CLASS}>Organization name *</label>
          <input {...field('orgName')} className={INPUT_CLASS} autoComplete="organization" required />
        </div>
        <fieldset>
          <legend className={LABEL_CLASS}>Organization type(s) *</legend>
          <p className="mb-2 text-xs text-gray-400">Select all that describe your organization.</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {ORG_TYPES.map(({ value, label }) => (
              <label key={value} className="flex items-center gap-2 rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-700 cursor-pointer">
                <input type="checkbox" checked={form.businessTypes.includes(value)} onChange={() => toggleType(value)} disabled={loading} />
                {label}
              </label>
            ))}
          </div>
        </fieldset>
        <div>
          <label htmlFor="signup-address" className={LABEL_CLASS}>Address</label>
          <textarea {...field('address')} className={INPUT_CLASS} rows={2} autoComplete="street-address" />
        </div>
        {/*
          A field that appears when a box is ticked reads as one the box made mandatory, so the label
          says "(optional)" and the hint says why it showed up and that the form submits without it.
        */}
        {collectsTaxId && (
          <div>
            <label htmlFor="signup-taxId" className={LABEL_CLASS}>Tax ID / EIN (optional)</label>
            <input {...field('taxId')} className={INPUT_CLASS} />
            <p className="mt-1 text-xs text-gray-400">
              We ask this of organizations we pay out to. Adding it now speeds up payment setup — you can create your account without it.
            </p>
          </div>
        )}
      </fieldset>

      {/* The documents open in a new tab so following one does not discard a half-finished form. */}
      <label className="flex items-start gap-2 text-sm text-gray-700">
        <input type="checkbox" className="mt-1" checked={agreed} onChange={(e) => setAgreed(e.target.checked)} disabled={loading} data-testid="signup-agree" />
        <span>
          I have read and agree to the{' '}
          <a href={`${portalBase}/terms`} target="_blank" rel="noopener" className="font-medium text-primary hover:underline">Terms of Service</a> and the{' '}
          <a href={`${portalBase}/privacy`} target="_blank" rel="noopener" className="font-medium text-primary hover:underline">Privacy Policy</a>.
        </span>
      </label>

      {error && <p className="rounded-xl bg-red-50 px-4 py-3 text-sm text-red-700" role="alert" data-testid="signup-error">{error}</p>}

      <button type="submit" className="btn-primary w-full px-6 py-3 rounded-full font-medium disabled:opacity-50 disabled:cursor-not-allowed" disabled={loading || !isValid}>
        {loading ? 'Creating account…' : 'Create account'}
      </button>

      <p className="text-center text-xs text-gray-400">Your organization is reviewed for approval before you can sign in.</p>
    </form>
  );
};

export default SignUpForm;
